/**
 * EMoneyProbabilityOfSuccessBox component module.
 * @module advisor/shared/analyze/EMoneyProbabilityOfSuccessBox
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Column, DescriptiveText, Image, Row, EMoneyPoSChart} from '../../../shared';
import {connect} from 'react-redux';
import {_getPartners} from '../../reducers/partners';
import axiosInstance from '../../../shared/utils/axios';
import moment from 'moment';
import styled from 'styled-components/macro';
import {withSize} from 'react-sizeme';

const _Spacer = styled.div`
  height: 30px;
  visibility: hidden;
  flex-shrink: 0;
`;

/**
 * EMoneyProbabilityOfSuccessBox component
 * @name EMoneyProbabilityOfSuccessBox
 */
const EMoneyProbabilityOfSuccessBox = (props) => {
  const {size, openModal, getPartners, householdId, session, queue, info, layoutSize} = props;
  const [partner, setPartner] = useState(null);
  const [retrieving, setRetrieving] = useState(false);
  const [processing, setProcessing] = useState(false);

  const isQueued = Boolean(queue?.isQueued);
  const clientId = info?.clientId;
  const lastUpdated = info?.lastUpdated ? moment.utc(info.lastUpdated, 'YYYY-MM-DD HH:mm:ss').format('M/D/YY') : null;
  const planId = info?.planId;
  const probability = info?.probabilityOfSuccess;
  const displayText = info?.probabilityOfSuccessTargetText;
  const error = displayText ? displayText.split(':').includes('Error') : null;
  const pos = probability ? Number(probability.substring(0, probability.length - 1)) : 0;

  useEffect(() => getPartners()
    .then((res) => {
      setPartner(res?.partners?.source?.find(item => item.name === 'emoney'));
    }), []);

  const modalButton = <Button label={'Update Info'} importance={'secondary'} size={'md'}
                              action={() => {
                                openModal({
                                  type: 'UpdateEMoneyInfoModal',
                                  title: 'Update Info',
                                  clientId: clientId,
                                  planId: planId,
                                  householdId: householdId,
                                  action: () => {
                                    setRetrieving(true);
                                  },
                                });
                              }}
  />;

  return <Column height={'100%'} width={'100%'} overflow={'hidden'}>
    {size.height > 236 ? <Row width={'100%'} layout={{shrink: 0}}>
      <Column width={'100%'} alignItems={'left'}>
        <Image src={partner?.logo} maxHeight={'30px'} maxWidth={'88px'}/>
      </Column>
    </Row>
      : null}
    <Row height={'100%'} width={'100%'}>
      {!pos && !(retrieving || isQueued) && <Column layout={{shrink: 0}}>
        <DescriptiveText>
          Press &quot;Update Info&quot; to add Client ID and Plan ID to retrieve Probability of Success.
        </DescriptiveText>
        {modalButton}
      </Column>}
      {!pos && (retrieving || isQueued) && <Column layout={{shrink: 0}}>
        <DescriptiveText>{session.profile.brand.name} is working to retrieve the Probability of Success from eMoney. Once data has been
          received from eMoney it will be populated on the Probability of Success tile.</DescriptiveText>
        {modalButton}
      </Column>}
      {!!pos && <Column height={'100%'} justifySubColumn={'middle'}>
        <Column overflow={'hidden'}>
          <EMoneyPoSChart probabilityOfSuccess={pos} layoutSize={layoutSize}/>
        </Column>
        <Column layout={{shrink: 0}}>
          <DescriptiveText margin={size.height > 236 ? '5px' : '0px'}>
            {lastUpdated ? 'Last Updated ' + lastUpdated : ''}<br/>
            {(retrieving || isQueued) && !error ? 'Your Request is Queued up. We’re working on Retrieving your Data.' : ''}
            {error ? 'Error Loading Data' : ''}
          </DescriptiveText>
          <div>
            {modalButton}
            <Button label={(retrieving || isQueued) ? 'Pending Refresh' : 'Refresh'} importance={'secondary'} size={'md'}
                    disabled={retrieving || isQueued}
                    processing={processing}
                    action={() => {
                      setProcessing(true);
                      axiosInstance.post(`/households/${householdId}/emoney`, {clientId: clientId, planId: planId})
                        .then(() => {
                          setRetrieving(true);
                        })
                        .catch((err) => {
                          setRetrieving(false);
                          openModal({type: 'Error', err});
                        })
                        .finally(() => {
                          setProcessing(false);
                        });
                    }}/>
          </div>
        </Column>
      </Column>}
    </Row>
    {size.height > 236 ? <_Spacer>&nbsp;</_Spacer> : null}
  </Column>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  getPartners: () => dispatch(_getPartners()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withSize({monitorHeight: true})(EMoneyProbabilityOfSuccessBox));

EMoneyProbabilityOfSuccessBox.propTypes = {
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    height: PropTypes.number,
  }),
  openModal: PropTypes.func.isRequired,
  /**
   * Gathers the list of partners the user has available to them
   */
  getPartners: PropTypes.func.isRequired,
  session: PropTypes.object,
  householdId: PropTypes.number.isRequired,
  queue: PropTypes.shape({
    isQueued: PropTypes.bool,
  }).isRequired,
  info: PropTypes.shape({
    clientId: PropTypes.string,
    planId: PropTypes.string,
    lastUpdated: PropTypes.string,
    probabilityOfSuccess: PropTypes.string,
    probabilityOfSuccessTargetText: PropTypes.string,
  }).isRequired,
  /**
   * The size of the component ('xs', 'sm', or 'md')
   */
  layoutSize: PropTypes.string,
};
